import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { IconButton } from '@components/iconButton';
import { Button } from '@components/button';
import { Tooltip } from '@components/tooltip/Tooltip';
import { TextField } from '@components/textField';
import { useUpdateQuote } from '@services/quotes';
import { usePostAutomaticFormUpdates } from '@services/quoteForm';
import { enqueueSnackbar } from 'notistack';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';

import { PenSolid } from '@assets/svgs';
import { ToastType } from '@components/toast';

interface EditableFormVersionProps {
  quoteId: string;
  formsVersion: number | null | undefined;
}

export const EditableFormVersion = ({ quoteId, formsVersion }: EditableFormVersionProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const { isAdmin, isPortalAdmin } = useStore((state) => state.userAuth, shallow);
  const { updateQuote, isPending } = useUpdateQuote();
  const { postAutomaticFormUpdatesAsync } = usePostAutomaticFormUpdates();

  const canEdit = isAdmin || isPortalAdmin;
  const displayValue = formsVersion !== null && formsVersion !== undefined ? formsVersion.toString() : '-';

  const handleStartEdit = () => {
    setEditValue(displayValue === '-' ? '' : displayValue);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue('');
  };

  const handleSave = async () => {
    const newVersion = editValue.trim() === '' ? undefined : Number(editValue);

    if (isNaN(newVersion as number)) {
      enqueueSnackbar('Please enter a valid number', {
        variant: ToastType.Error,
        autoHideDuration: 3000,
      });
      return;
    }

    if (newVersion !== undefined && newVersion <= 0) {
      enqueueSnackbar('Form version must be greater than 0', {
        variant: ToastType.Error,
        autoHideDuration: 3000,
      });
      return;
    }

    try {
      if (typeof newVersion !== 'number') {
        console.log('invalid save attempt: formsVersion is null or undefined, setting to', newVersion);
        return;
      }

      await updateQuote({
        quoteId,
        formsVersion: newVersion,
      });

      await postAutomaticFormUpdatesAsync({ quoteId });

      enqueueSnackbar('Quote Form Version updated successfully', {
        variant: ToastType.Success,
        autoHideDuration: 3000,
      });

      setIsEditing(false);
      setEditValue('');
    } catch (error: any) {
      let errorMessage = 'Failed to update Quote Form Version';

      try {
        const data = await error?.response?.json();
        if (data?.error?.message) {
          errorMessage = data.error.message;
        }
      } catch {
        if (error?.message) {
          errorMessage = error.message;
        }
      }

      enqueueSnackbar(errorMessage, {
        variant: ToastType.Error,
        autoHideDuration: 5000,
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <Box
      component='span'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: 0,
        m: 0,
        minWidth: 0,
        width: '100%',
        alignItems: 'flex-start',
      }}
    >
      <Typography
        component='span'
        variant='caption'
        sx={{
          display: 'block',
          color: 'text.subdued',
        }}
      >
        Quote Form Version
      </Typography>

      {isEditing ? (
        <Box component='span' sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
          <TextField
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            size='small'
            type='number'
            disabled={isPending}
            autoFocus
            inputProps={{ 'aria-label': 'Quote Form Version' }}
            sx={{
              '& .MuiInputBase-root': {
                height: '32px',
              },
            }}
          />
          <Box component='span' sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant='contained'
              onClick={handleSave}
              disabled={isPending}
              size='small'
              sx={{
                padding: 0,
              }}
            >
              Save
            </Button>
            <Button
              variant='text'
              onClick={handleCancel}
              disabled={isPending}
              size='small'
              sx={{
                padding: 0,
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      ) : (
        <Box component='span' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            component='span'
            variant='bodySmall'
            sx={{
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
              lineHeight: 1.4,
            }}
          >
            {displayValue}
          </Typography>
          {canEdit && (
            <Tooltip title='Edit Quote Form Version'>
              <IconButton aria-label='Edit Quote Form Version' onClick={handleStartEdit} sx={{ padding: '2px' }}>
                <PenSolid width='12px' height='12px' />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};
